// Editor de fluxo (/flowbuilder/:id) no padrão Aeros Dark — mockup "Canvas
// Flow A": topbar 56px (voltar · nome editável · pill ativo · "salvo há" ·
// desfazer/refazer · Testar · Versões · Salvar), paleta 232px com busca e
// grupos em grade 2 colunas, canvas pontilhado com arestas em gradiente,
// minimap + zoom no canto, aviso de validação (regras puras em flowRules)
// e painel de propriedades 300px do nó selecionado. Undo/redo, Testar fluxo
// e Versões ficam desabilitados (dependem de backend — ver CONTEXT.md).
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import Drawer from "@material-ui/core/Drawer";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  ArrowBack,
  Edit,
  CloudDone,
  Undo,
  Redo,
  SmartToy,
  History,
  Save,
  Search,
  Add,
  Remove,
  FitScreen,
  ErrorOutline,
  ContentCopy,
  DeleteOutline,
  Close,
} from "@mui/icons-material";
import ReactFlow, {
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
} from "react-flow-renderer";

import "./aerosFlow.css";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useNodeStorage } from "../../stores/useNodeStorage";
import {
  NODE_META,
  TONES,
  filterPalette,
  findFlowProblems,
  problemText,
  nodeTitle,
  nodeSummary,
  savedAgo,
} from "./flowRules";
import { NodeTile } from "./nodes/AerosNode";

import messageNode from "./nodes/messageNode";
import startNode from "./nodes/startNode";
import openaiNode from "./nodes/openaiNode";
import geminiNode from "./nodes/geminiNode";
import menuNode from "./nodes/menuNode";
import intervalNode from "./nodes/intervalNode";
import imgNode from "./nodes/imgNode";
import randomizerNode from "./nodes/randomizerNode";
import videoNode from "./nodes/videoNode";
import switchFlowNode from "./nodes/switchFlowNode";
import attendantNode from "./nodes/attendantNode";
import RemoveEdge from "./nodes/removeEdge";
import audioNode from "./nodes/audioNode";
import singleBlockNode from "./nodes/singleBlockNode";
import ticketNode from "./nodes/ticketNode";
import tagNode from "./nodes/tagNode";
import HttpRequestNode from "./nodes/httpRequestNode";
import removeTagNode from "./nodes/removeTagNode";
import VariableNode, { getFlowVariable, setFlowVariable } from "./nodes/variableNode";
import inputNode from "./nodes/inputNode";
import conditionCompareNode from "./nodes/conditionCompareNode";

import FlowBuilderAddImgModal from "../../components/FlowBuilderAddImgModal";
import FlowBuilderTicketModal from "../../components/FlowBuilderAddTicketModal";
import FlowBuilderAddAudioModal from "../../components/FlowBuilderAddAudioModal";
import FlowBuilderAddTagModal from "../../components/FlowBuilderAddTagModal";
import FlowBuilderRandomizerModal from "../../components/FlowBuilderRandomizerModal";
import FlowBuilderAddVideoModal from "../../components/FlowBuilderAddVideoModal";
import FlowBuilderSingleBlockModal from "../../components/FlowBuilderSingleBlockModal";
import FlowBuilderAddTextModal from "../../components/FlowBuilderAddTextModal";
import FlowBuilderIntervalModal from "../../components/FlowBuilderIntervalModal";
import FlowBuilderMenuModal from "../../components/FlowBuilderMenuModal";
import FlowBuilderAddSwitchFlowModal from "../../components/FlowBuilderAddSwitchFlowModal";
import FlowBuilderAddAttendantModal from "../../components/FlowBuilderAddAttendantModal";
import FlowBuilderInputModal from "../../components/FlowBuilderInputModal";
import FlowBuilderRemoveTagModal from "../../components/FlowBuilderRemoveTagModal";
import FlowBuilderOpenAIModal from "../../components/FlowBuilderAddOpenAIModal";
import FlowBuilderGeminiModal from "../../components/FlowBuilderGeminiModal";
import FlowBuilderConditionCompareModal from "../../components/FlowBuilderConditionCompareModal";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const borderStrong = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)";
  const cyanText = stateText(theme, AEROS.cyan, "info");
  const ghostButton = {
    border: `1px solid ${borderStrong}`,
    borderRadius: 9,
    padding: "6px 12px",
    fontSize: 12.5,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: "none",
    whiteSpace: "nowrap",
    "& .MuiButton-startIcon": { marginRight: 6 },
    "& .MuiSvgIcon-root": { fontSize: 16 },
    "&.Mui-disabled": { color: theme.palette.text.secondary, opacity: 0.6 },
  };
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      backgroundColor: theme.palette.aeros.background,
      fontSize: 14,
    },
    topbar: {
      height: 56,
      flex: "none",
      display: "flex",
      alignItems: "center",
      padding: "0 20px",
      borderBottom: `1px solid ${line}`,
      gap: 12,
      [theme.breakpoints.down("sm")]: { padding: "0 12px", gap: 8, overflowX: "auto" },
    },
    back: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      background: "none",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      padding: 0,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:hover": { color: theme.palette.text.primary },
    },
    sep: { color: theme.palette.aeros.textTertiary },
    titleWrap: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 320,
    },
    titleInput: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${AEROS.cyan}`,
      minWidth: 200,
    },
    editIcon: {
      padding: 2,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15 },
      "&:hover": { color: theme.palette.text.primary, background: "none" },
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      whiteSpace: "nowrap",
      "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },
    },
    pillOn: { color: stateText(theme, AEROS.success, "success"), background: alpha(AEROS.success, 0.1) },
    pillOff: { color: theme.palette.text.secondary, background: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)" },
    saved: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "center",
      gap: 5,
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    savedDirty: { color: stateText(theme, AEROS.warning, "warning") },
    actions: { marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flex: "none" },
    iconGroup: { display: "flex", border: `1px solid ${borderStrong}`, borderRadius: 9, overflow: "hidden" },
    iconGroupBtn: {
      width: 32,
      height: 32,
      borderRadius: 0,
      color: theme.palette.text.secondary,
      "& + &": { borderLeft: `1px solid ${borderStrong}` },
      "& .MuiSvgIcon-root": { fontSize: 17 },
      "&.Mui-disabled": { color: theme.palette.aeros.textTertiary },
    },
    ghost: ghostButton,
    save: {
      borderRadius: 9,
      padding: "6px 14px",
      fontSize: 12.5,
      fontWeight: 700,
      whiteSpace: "nowrap",
      "& .MuiButton-startIcon": { marginRight: 6 },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    body: { flex: 1, minHeight: 0, display: "flex" },
    // ---- paleta ----
    palette: {
      width: 232,
      flex: "none",
      backgroundColor: theme.palette.aeros.sidebar,
      borderRight: `1px solid ${line}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    paletteSearchWrap: { padding: "14px 14px 10px" },
    paletteSearch: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 9,
      padding: "4px 10px",
      fontSize: 12,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      width: "100%",
      "& .MuiSvgIcon-root": { fontSize: 15, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    paletteBody: {
      padding: "0 14px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      overflowY: "auto",
      flex: 1,
      minHeight: 0,
      ...theme.scrollbarStyles,
    },
    groupLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 5,
    },
    groupGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 },
    block: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 8,
      padding: "5px 7px",
      display: "flex",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
      minWidth: 0,
      font: "inherit",
      color: theme.palette.text.primary,
      textAlign: "left",
      "&:hover": { borderColor: "rgba(160,68,255,0.45)", backgroundColor: isLight ? theme.palette.aeros.surface2 : "#151926" },
    },
    blockLabel: { fontSize: 10.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 },
    // ---- canvas ----
    canvas: {
      flex: 1,
      minWidth: 0,
      position: "relative",
      backgroundColor: theme.palette.aeros.background,
      "& .react-flow__minimap": {
        backgroundColor: isLight ? "rgba(255,255,255,0.92)" : "rgba(14,17,25,0.9)",
        border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      },
    },
    zoomCluster: {
      position: "absolute",
      right: 16,
      bottom: 16,
      zIndex: 5,
      display: "flex",
      backgroundColor: isLight ? "rgba(255,255,255,0.92)" : "rgba(14,17,25,0.9)",
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    zoomBtn: {
      width: 32,
      height: 32,
      borderRadius: 0,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    zoomValue: {
      padding: "0 10px",
      display: "flex",
      alignItems: "center",
      borderLeft: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRight: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      fontVariantNumeric: "tabular-nums",
    },
    warn: {
      position: "absolute",
      left: 16,
      bottom: 16,
      zIndex: 5,
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: isLight ? "rgba(255,255,255,0.95)" : "rgba(14,17,25,0.95)",
      border: "1px solid rgba(248,113,113,0.35)",
      borderRadius: 10,
      padding: "8px 12px",
      fontSize: 12,
      color: theme.palette.text.secondary,
      maxWidth: "calc(100% - 220px)",
      "& b": { color: theme.palette.text.primary },
      "& > .MuiSvgIcon-root": { fontSize: 16, color: AEROS.error, flex: "none" },
    },
    warnText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    warnLink: {
      color: cyanText,
      marginLeft: 6,
      background: "none",
      border: "none",
      cursor: "pointer",
      // `font: inherit` zera weight/size, entao os dois vem depois dele.
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      padding: 0,
      whiteSpace: "nowrap",
      "&:hover": { textDecoration: "underline" },
    },
    // ---- painel ----
    panel: {
      width: 300,
      flex: "none",
      backgroundColor: theme.palette.aeros.sidebar,
      borderLeft: `1px solid ${line}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    panelHead: { padding: "14px 16px", borderBottom: `1px solid ${line}`, display: "flex", alignItems: "center", gap: 10 },
    panelTitle: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary },
    panelSub: { fontSize: 10.5, color: theme.palette.text.secondary },
    panelIcon: {
      padding: 4,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 17 },
      "&:hover": { color: theme.palette.text.primary, background: "none" },
    },
    panelIconDanger: { color: AEROS.error, "&:hover": { color: AEROS.error } },
    panelBody: { padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", ...theme.scrollbarStyles },
    fieldLabel: { fontSize: 11.5, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 6 },
    fieldBox: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 9,
      padding: "9px 11px",
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
    },
    outRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 9,
      padding: "7px 10px",
      fontSize: 12,
      color: theme.palette.text.primary,
      "& i": { width: 6, height: 6, borderRadius: "50%", flex: "none" },
      "& small": { marginLeft: "auto", fontSize: 10.5, color: theme.palette.text.secondary },
    },
    panelEmpty: { padding: 16, fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.5 },
    problem: { fontSize: 11.5, color: AEROS.error, display: "flex", gap: 6, alignItems: "flex-start", "& .MuiSvgIcon-root": { fontSize: 14, flex: "none", marginTop: 1 } },
    editBlock: {
      borderRadius: 9,
      padding: "7px 12px",
      fontSize: 12.5,
      fontWeight: 700,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    mobileAdd: {
      position: "absolute",
      left: 16,
      bottom: 64,
      zIndex: 5,
      borderRadius: 10,
      padding: "8px 14px",
      fontSize: 12.5,
      fontWeight: 700,
    },
    drawerPaper: {
      backgroundColor: theme.palette.aeros.sidebar,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      maxHeight: "70vh",
      display: "flex",
      flexDirection: "column",
    },
    loading: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" },
  };
});

const nodeTypes = {
  message: messageNode,
  start: startNode,
  menu: menuNode,
  interval: intervalNode,
  img: imgNode,
  gemini: geminiNode,
  audio: audioNode,
  randomizer: randomizerNode,
  video: videoNode,
  singleBlock: singleBlockNode,
  ticket: ticketNode,
  tag: tagNode,
  removeTag: removeTagNode,
  switchFlow: switchFlowNode,
  attendant: attendantNode,
  httpRequest: HttpRequestNode,
  variable: VariableNode,
  openai: openaiNode,
  input: inputNode,
  conditionCompare: conditionCompareNode,
};

const edgeTypes = { buttonedge: RemoveEdge };

const initialNodes = [{ id: "1", position: { x: 250, y: 100 }, data: { label: "Inicio do fluxo" }, type: "start" }];

const rid = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < 30; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
};

// chave da paleta / do modal → { type do nó, data } a partir do payload do modal
const buildNode = (kind, d = {}) => {
  switch (kind) {
    case "text":
      return { type: "message", data: { label: d.text } };
    case "interval":
      return { type: "interval", data: { label: `Intervalo ${d.sec} seg.`, sec: d.sec } };
    case "menu":
      return { type: "menu", data: { message: d.message, arrayOption: d.arrayOption, typeMenu: d.typeMenu } };
    case "img":
    case "video":
      return { type: kind, data: { url: d.url } };
    case "audio":
      return { type: "audio", data: { url: d.url, record: d.record } };
    case "random":
      return { type: "randomizer", data: { percent: d.percent } };
    case "content":
      return { type: "singleBlock", data: { ...d } };
    case "condition":
      return { type: "conditionCompare", data: { ...d } };
    case "removeTag":
      return { type: "removeTag", data: { tag: d?.tag || "", ...d } };
    case "httpRequest":
      return {
        type: "httpRequest",
        data: {
          url: "",
          method: d?.method || "POST",
          requestBody: d?.requestBody || "{}",
          headersString: d?.headersString || "",
          queryParams: d?.queryParams || [],
          saveVariables: d?.saveVariables || [],
          ...d,
        },
      };
    case "variable":
      return {
        type: "variable",
        data: {
          variableName: d?.variableName || "",
          variableValue: d?.variableValue || "",
          variableType: d?.variableType || "text",
          variableExpression: d?.variableExpression || "",
          isExpression: d?.isExpression || false,
          ...d,
        },
      };
    case "input":
      return { type: "input", data: { question: d?.question || "", variableName: d?.variableName || "", ...d } };
    default:
      // ticket, tag, switchFlow, attendant, openai, gemini
      return { type: kind, data: { ...d } };
  }
};

// nó → chave do modal que o edita (null = editado inline no canvas)
const MODAL_OF = {
  message: "text",
  interval: "interval",
  menu: "menu",
  img: "img",
  audio: "audio",
  randomizer: "random",
  video: "video",
  singleBlock: "content",
  ticket: "ticket",
  tag: "tag",
  removeTag: "removeTag",
  switchFlow: "switchFlow",
  openai: "openai",
  gemini: "gemini",
  attendant: "attendant",
  input: "input",
  conditionCompare: "condition",
};
const INLINE_ADD = ["httpRequest", "variable"];

// garante responseVariables nos nós HTTP (contrato do backend)
const prepareHttpNode = (node) => {
  if (node.type !== "httpRequest") return node;
  const d = node.data || {};
  if (d.saveVariables?.length) {
    if (!Array.isArray(d.responseVariables)) {
      d.responseVariables = d.saveVariables.map((it) => ({ path: it.path, variableName: it.variable }));
    }
  } else {
    d.saveVariables = d.saveVariables || [];
    d.responseVariables = d.responseVariables || [];
  }
  return { ...node, data: d };
};

const ZoomCluster = ({ classes }) => {
  const { zoomIn, zoomOut, fitView, getZoom } = useReactFlow();
  const [zoom, setZoom] = useState(100);
  useEffect(() => {
    const t = setInterval(() => setZoom(Math.round(getZoom() * 100)), 300);
    return () => clearInterval(t);
  }, [getZoom]);
  return (
    <div className={classes.zoomCluster}>
      <IconButton className={classes.zoomBtn} onClick={() => zoomOut()} aria-label={i18n.t("aeros.flow.editor.zoomOut")}><Remove /></IconButton>
      <div className={classes.zoomValue}>{zoom}%</div>
      <IconButton className={classes.zoomBtn} onClick={() => zoomIn()} aria-label={i18n.t("aeros.flow.editor.zoomIn")}><Add /></IconButton>
      <IconButton className={classes.zoomBtn} style={{ borderLeft: "1px solid rgba(128,128,128,0.2)" }} onClick={() => fitView({ padding: 0.2 })} aria-label={i18n.t("aeros.flow.editor.fit")}><FitScreen /></IconButton>
    </div>
  );
};

const ProblemBanner = ({ classes, problems, nodes, onGoTo }) => {
  const { setCenter } = useReactFlow();
  if (!problems.length) return null;
  const p = problems[0];
  const goTo = () => {
    const n = nodes.find((x) => x.id === p.nodeId);
    if (!n) return;
    onGoTo(n.id);
    setCenter(n.position.x + (n.width || 200) / 2, n.position.y + (n.height || 80) / 2, { zoom: 1, duration: 400 });
  };
  return (
    <div className={classes.warn} role="status">
      <ErrorOutline />
      <span className={classes.warnText}>
        <b>{i18n.t("aeros.flow.problems.count", { count: problems.length })}</b> · {problemText(p, nodes)}
      </span>
      {p.nodeId && (
        <button type="button" className={classes.warnLink} onClick={goTo}>
          {i18n.t("aeros.flow.problems.goTo")}
        </button>
      )}
    </div>
  );
};

const Palette = ({ classes, onPick }) => {
  const [q, setQ] = useState("");
  const groups = filterPalette(q);
  return (
    <>
      <div className={classes.paletteSearchWrap}>
        <InputBase
          className={classes.paletteSearch}
          placeholder={i18n.t("aeros.flow.editor.searchBlock")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          startAdornment={<Search />}
          inputProps={{ "aria-label": i18n.t("aeros.flow.editor.searchBlock") }}
        />
      </div>
      <div className={classes.paletteBody}>
        {groups.map((g) => (
          <div key={g.key}>
            <div className={classes.groupLabel}>{i18n.t(g.labelKey)}</div>
            <div className={classes.groupGrid}>
              {g.types.map((type) => (
                <button type="button" key={type} className={classes.block} onClick={() => onPick(NODE_META[type].palette)}>
                  <NodeTile type={type} size={20} iconSize={12} radius={6} />
                  <span className={classes.blockLabel}>{i18n.t(NODE_META[type].labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export const FlowBuilderConfig = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [flowMeta, setFlowMeta] = useState({ name: "", active: true, updatedAt: null });
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [tick, setTick] = useState(0); // re-render do "salvo há X min"
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // { kind, mode: "create"|"edit" }
  const [dataNode, setDataNode] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const [nodes, setNodes, onNodesChangeBase] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState([]);
  const storage = useNodeStorage();
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  useEffect(() => {
    window.flowVariables = window.flowVariables || {};
    window.getFlowVariable = getFlowVariable;
    window.setFlowVariable = setFlowVariable;
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // ---- carga ----
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/flowbuilder/flow/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        const flow = data.flow || {};
        setFlowMeta({ name: flow.name || "", active: flow.active !== false, updatedAt: flow.updatedAt || null });
        if (flow.flow) {
          // `style` gravado por versões antigas (fundo cinza) sobrescreveria o visual Aeros
          setNodes((flow.flow.nodes || []).map((n) => prepareHttpNode({ ...n, style: undefined, selected: false })));
          setEdges(flow.flow.connections || []);
        }
        setDirty(false);
      })
      .catch(toastError)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, setNodes, setEdges]);

  // ---- duplicar / excluir vindos do menu "…" do nó ----
  useEffect(() => {
    if (storage.action === "delete") {
      setNodes((old) => old.filter((n) => n.id !== storage.node));
      setEdges((old) => old.filter((e) => e.source !== storage.node && e.target !== storage.node));
      setDirty(true);
    }
    if (storage.action === "duplicate") {
      const src = nodesRef.current.find((n) => n.id === storage.node);
      if (src) {
        const maxX = Math.max(...nodesRef.current.map((n) => n.position.x));
        setNodes((old) => [
          ...old.map((n) => ({ ...n, selected: false })),
          { ...src, id: rid(), position: { x: maxX + 240, y: src.position.y }, selected: true, data: JSON.parse(JSON.stringify(src.data || {})) },
        ]);
        setDirty(true);
      }
    }
    if (storage.action !== "idle") {
      storage.setNodesStorage("");
      storage.setAct("idle");
    }
  }, [storage.action]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNodesChange = useCallback(
    (changes) => {
      if (changes.some((c) => c.type !== "select" && c.type !== "dimensions")) setDirty(true);
      onNodesChangeBase(changes);
    },
    [onNodesChangeBase]
  );
  const onEdgesChange = useCallback(
    (changes) => {
      if (changes.some((c) => c.type !== "select")) setDirty(true);
      onEdgesChangeBase(changes);
    },
    [onEdgesChangeBase]
  );
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
      setDirty(true);
    },
    [setEdges]
  );

  // ---- adicionar nó ----
  const addNode = (kind, payload) => {
    const last = nodesRef.current[nodesRef.current.length - 1] || initialNodes[0];
    const position = { x: last.position.x + (last.width || 200) + 40, y: last.position.y };
    const built = buildNode(kind, payload);
    setNodes((old) => [...old.map((n) => ({ ...n, selected: false })), { id: rid(), position, selected: true, ...built }]);
    setDirty(true);
  };

  const updateNode = (altered) => {
    setNodes((old) => old.map((n) => (n.id === altered.id ? { ...altered, style: undefined } : n)));
    setDirty(true);
    setModal(null);
  };

  const openEditor = (node) => {
    if (!node) return;
    setDataNode(prepareHttpNode(node));
    const kind = MODAL_OF[node.type];
    if (kind) setModal({ kind, mode: "edit" });
  };

  const pick = (kind) => {
    setDrawer(false);
    if (INLINE_ADD.includes(kind)) addNode(kind, {});
    else setModal({ kind, mode: "create" });
  };

  // ---- salvar / renomear ----
  const saveFlow = async () => {
    setSaving(true);
    try {
      const processed = nodesRef.current.map(prepareHttpNode);
      await api.post("/flowbuilder/flow", { idFlow: id, nodes: processed, connections: edges });
      setNodes(processed);
      setDirty(false);
      setFlowMeta((m) => ({ ...m, updatedAt: new Date().toISOString() }));
      toast.success(i18n.t("aeros.flow.editor.saved"));
    } catch (err) {
      toast.error(i18n.t("aeros.flow.editor.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const commitName = async () => {
    setEditingName(false);
    const name = nameDraft.trim();
    if (!name || name === flowMeta.name) return;
    try {
      await api.put("/flowbuilder", { name, flowId: id });
      setFlowMeta((m) => ({ ...m, name }));
      toast.success(i18n.t("aeros.flow.newFlow.renamed"));
    } catch (err) {
      toastError(err);
    }
  };

  const selectNode = (nodeId) =>
    setNodes((old) => old.map((n) => ({ ...n, selected: n.id === nodeId })));

  // ---- derivados ----
  const problems = useMemo(() => findFlowProblems(nodes, edges), [nodes, edges]);
  const selected = nodes.find((n) => n.selected);
  const selectedProblems = selected ? problems.filter((p) => p.nodeId === selected.id) : [];
  const outputs = useMemo(() => {
    if (!selected) return [];
    return edges
      .filter((e) => e.source === selected.id)
      .map((e) => ({ handle: e.sourceHandle || "a", target: nodes.find((n) => n.id === e.target) }));
  }, [selected, edges, nodes]);
  const ago = savedAgo(flowMeta.updatedAt, Date.now() + tick * 0);

  const modalProps = (kind) => ({
    open: modal?.kind === kind ? modal.mode : null,
    onSave: (data) => addNode(kind, data),
    data: dataNode,
    onUpdate: updateNode,
    close: () => setModal(null),
  });

  const dotsColor = theme.palette.type === "light" ? "rgba(15,20,40,0.12)" : "rgba(255,255,255,0.07)";

  return (
    <div className={classes.root}>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="aerosEdge" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="1200" y2="0">
            <stop offset="0" stopColor={theme.palette.primary.main} />
            <stop offset="1" stopColor={AEROS.cyan} />
          </linearGradient>
        </defs>
      </svg>

      {/* ---- topbar ---- */}
      <div className={classes.topbar}>
        <button type="button" className={classes.back} onClick={() => history.push("/flowbuilders")}>
          <ArrowBack /> {i18n.t("aeros.flow.editor.back")}
        </button>
        <span className={classes.sep}>/</span>
        <div className={classes.titleWrap}>
          {editingName ? (
            <InputBase
              className={classes.titleInput}
              value={nameDraft}
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitName();
                if (e.key === "Escape") setEditingName(false);
              }}
              inputProps={{ "aria-label": i18n.t("aeros.flow.editor.rename") }}
            />
          ) : (
            <>
              <div className={classes.title}>{flowMeta.name}</div>
              <IconButton
                className={classes.editIcon}
                aria-label={i18n.t("aeros.flow.editor.rename")}
                onClick={() => {
                  setNameDraft(flowMeta.name);
                  setEditingName(true);
                }}
              >
                <Edit />
              </IconButton>
            </>
          )}
        </div>
        {!isMobile && (
          <>
            <span className={`${classes.pill} ${flowMeta.active ? classes.pillOn : classes.pillOff}`}>
              <i />
              {i18n.t(flowMeta.active ? "aeros.flow.list.active" : "aeros.flow.list.inactive")}
            </span>
            <span className={`${classes.saved} ${dirty ? classes.savedDirty : ""}`}>
              {!dirty && <CloudDone />}
              {dirty ? i18n.t("aeros.flow.editor.unsaved") : i18n.t(ago.key, { count: ago.count })}
            </span>
          </>
        )}
        <div className={classes.actions}>
          {!isMobile && (
            <>
              <Tooltip title={i18n.t("aeros.flow.editor.soon")}>
                <div className={classes.iconGroup}>
                  <IconButton className={classes.iconGroupBtn} disabled aria-label={i18n.t("aeros.flow.editor.undo")}><Undo /></IconButton>
                  <IconButton className={classes.iconGroupBtn} disabled aria-label={i18n.t("aeros.flow.editor.redo")}><Redo /></IconButton>
                </div>
              </Tooltip>
              <Tooltip title={i18n.t("aeros.flow.editor.soon")}>
                <span><Button className={classes.ghost} startIcon={<SmartToy />} disabled>{i18n.t("aeros.flow.editor.test")}</Button></span>
              </Tooltip>
              <Tooltip title={i18n.t("aeros.flow.editor.soon")}>
                <span><Button className={classes.ghost} startIcon={<History />} disabled>{i18n.t("aeros.flow.editor.versions")}</Button></span>
              </Tooltip>
            </>
          )}
          <Button variant="contained" color="primary" className={classes.save} startIcon={<Save />} onClick={saveFlow} disabled={saving || loading}>
            {i18n.t("aeros.flow.editor.save")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={classes.loading}><CircularProgress color="primary" /></div>
      ) : (
        <div className={classes.body}>
          {!isMobile && (
            <div className={classes.palette}>
              <Palette classes={classes} onPick={pick} />
            </div>
          )}

          <div className={`${classes.canvas} aeros-flow`}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              deleteKeyCode={["Backspace", "Delete"]}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeDoubleClick={(e, node) => openEditor(node)}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              minZoom={0.2}
              connectionLineStyle={{ stroke: AEROS.cyan, strokeWidth: 2 }}
              defaultEdgeOptions={{ style: { strokeWidth: 2 } }}
              proOptions={{ hideAttribution: true }}
            >
              <Background variant="dots" gap={22} size={1} color={dotsColor} />
              {!isMobile && (
                <MiniMap
                  nodeColor={(n) => (n.type === "start" ? AEROS.success : "#9CA3B8")}
                  nodeStrokeWidth={0}
                  maskColor={theme.palette.type === "light" ? "rgba(15,20,40,0.06)" : "rgba(11,13,20,0.55)"}
                  style={{ background: "transparent" }}
                />
              )}
              <ZoomCluster classes={classes} />
              <ProblemBanner classes={classes} problems={problems} nodes={nodes} onGoTo={selectNode} />
              {isMobile && (
                <Button variant="contained" color="primary" className={classes.mobileAdd} startIcon={<Add />} onClick={() => setDrawer(true)}>
                  {i18n.t("aeros.flow.editor.addBlocks")}
                </Button>
              )}
            </ReactFlow>
          </div>

          {!isMobile && (
            <div className={classes.panel}>
              {selected ? (
                <>
                  <div className={classes.panelHead}>
                    <NodeTile type={selected.type} size={30} iconSize={17} radius={9} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.panelTitle}>{nodeTitle(selected)}</div>
                      <div className={classes.panelSub}>
                        {i18n.t("aeros.flow.panel.nodeId", { id: selected.id.slice(0, 6) })}
                        {selected.type === "menu" && ` · ${i18n.t("aeros.flow.panel.options", { count: (selected.data?.arrayOption || []).length })}`}
                      </div>
                    </div>
                    {selected.type !== "start" && (
                      <>
                        <IconButton
                          className={classes.panelIcon}
                          aria-label={i18n.t("aeros.flow.node.duplicate")}
                          onClick={() => {
                            storage.setNodesStorage(selected.id);
                            storage.setAct("duplicate");
                          }}
                        >
                          <ContentCopy />
                        </IconButton>
                        <IconButton
                          className={`${classes.panelIcon} ${classes.panelIconDanger}`}
                          aria-label={i18n.t("aeros.flow.node.delete")}
                          onClick={() => {
                            storage.setNodesStorage(selected.id);
                            storage.setAct("delete");
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </>
                    )}
                  </div>
                  <div className={classes.panelBody}>
                    <div>
                      <div className={classes.fieldLabel}>{i18n.t("aeros.flow.panel.type")}</div>
                      <div className={classes.fieldBox} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: TONES[NODE_META[selected.type]?.tone || "msg"].color }} />
                        {nodeTitle(selected)}
                      </div>
                    </div>
                    {nodeSummary(selected) && (
                      <div>
                        <div className={classes.fieldLabel}>{i18n.t("aeros.flow.panel.summary")}</div>
                        <div className={classes.fieldBox}>{nodeSummary(selected)}</div>
                      </div>
                    )}
                    {selected.type === "menu" && (
                      <div>
                        <div className={classes.fieldLabel}>{i18n.t("aeros.flow.panel.outputs")}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {(selected.data?.arrayOption || []).map((o) => {
                            const out = outputs.find((x) => x.handle === `a${o.number}`);
                            return (
                              <div key={o.number} className={classes.outRow}>
                                <span style={{ width: 18, height: 18, borderRadius: 5, background: TONES.msg.bg, color: TONES.msg.color, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{o.number}</span>
                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.value}</span>
                                <small>{out?.target ? `→ ${nodeTitle(out.target)}` : i18n.t("aeros.flow.panel.notConnected")}</small>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {selected.type !== "menu" && outputs.length > 0 && (
                      <div>
                        <div className={classes.fieldLabel}>{i18n.t("aeros.flow.panel.outputs")}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {outputs.map((o, i) => (
                            <div key={i} className={classes.outRow}>
                              <i style={{ background: o.handle === "false" ? AEROS.error : o.handle === "true" ? AEROS.success : AEROS.cyan }} />
                              <span style={{ flex: 1 }}>{o.handle === "a" ? i18n.t("aeros.flow.panel.connected") : o.handle}</span>
                              <small>{o.target ? `→ ${nodeTitle(o.target)}` : ""}</small>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedProblems.map((p, i) => (
                      <div key={i} className={classes.problem}>
                        <ErrorOutline /> {problemText(p, nodes)}
                      </div>
                    ))}
                    {MODAL_OF[selected.type] ? (
                      <Button variant="contained" color="primary" className={classes.editBlock} startIcon={<Edit />} onClick={() => openEditor(selected)}>
                        {i18n.t("aeros.flow.panel.editBlock")}
                      </Button>
                    ) : (
                      selected.type !== "start" && <div className={classes.panelSub}>{i18n.t("aeros.flow.panel.noEditor")}</div>
                    )}
                  </div>
                </>
              ) : (
                <div className={classes.panelEmpty}>{i18n.t("aeros.flow.panel.empty")}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* paleta em gaveta no mobile */}
      <Drawer anchor="bottom" open={drawer} onClose={() => setDrawer(false)} classes={{ paper: classes.drawerPaper }}>
        <div className={classes.panelHead}>
          <div className={classes.panelTitle}>{i18n.t("aeros.flow.editor.addBlocks")}</div>
          <IconButton className={classes.panelIcon} style={{ marginLeft: "auto" }} onClick={() => setDrawer(false)} aria-label={i18n.t("aeros.header.close")}><Close /></IconButton>
        </div>
        <Palette classes={classes} onPick={pick} />
      </Drawer>

      {/* ---- modais dos blocos ---- */}
      <FlowBuilderAddTextModal {...modalProps("text")} />
      <FlowBuilderIntervalModal {...modalProps("interval")} />
      <FlowBuilderMenuModal {...modalProps("menu")} />
      <FlowBuilderAddImgModal {...modalProps("img")} />
      <FlowBuilderAddAudioModal {...modalProps("audio")} />
      <FlowBuilderRandomizerModal {...modalProps("random")} />
      <FlowBuilderAddVideoModal {...modalProps("video")} />
      <FlowBuilderSingleBlockModal {...modalProps("content")} />
      <FlowBuilderTicketModal {...modalProps("ticket")} />
      <FlowBuilderOpenAIModal {...modalProps("openai")} />
      <FlowBuilderGeminiModal {...modalProps("gemini")} />
      <FlowBuilderAddTagModal {...modalProps("tag")} />
      <FlowBuilderRemoveTagModal {...modalProps("removeTag")} />
      <FlowBuilderAddSwitchFlowModal {...modalProps("switchFlow")} />
      <FlowBuilderAddAttendantModal {...modalProps("attendant")} />
      <FlowBuilderInputModal {...modalProps("input")} />
      <FlowBuilderConditionCompareModal {...modalProps("condition")} nodes={nodes} edges={edges} />
    </div>
  );
};

export default FlowBuilderConfig;
